import {ADYEN_BLOCKED_PAYMENT_METHODS} from './../../overrides/app/util/constants'
import Logger from './../../overrides/app/controllers/logger'
import {
    isExpressShipping,
    isOmnichannelBasket
} from './../../overrides/app/controllers/util/adyenHelper'

/**
 * Filters and returns a list of payment methods based on various criteria such as country code, site ID, and express shipping status.
 *
 * @param {Object} response - The response object containing available payment methods.
 * @param {string} countryCode - The ISO 3166-1 alpha-2 country code used to filter payment methods.
 * @param {string} siteId - The identifier for the site, used for logging purposes.
 * @param {boolean} [isExpress=false] - Indicates whether the express shipping method is being used.
 * @param {Object} basket - The current shopping basket, used to determine if express shipping is applicable.
 * @param {string} shipToCountry - The country to which the order is being shipped.
 * @returns {Object} An object containing a filtered list of payment methods, each with relevant details such as type, name, brands, and configuration.
 *
 * @description
 * This function processes a list of payment methods from the provided response object and filters them based on predefined rules and conditions:
 * - Excludes blocked payment methods and those involving bank transfers.
 * - Filters specific payment methods based on the country code, such as 'bcmc' for Belgium and 'alipay' for China.
 * - For 'scheme' type methods, only includes brands like Amex, Visa, and Mastercard.
 * - Configures additional details for certain payment methods, such as 'paypal' and 'applepay'.
 * - Logs the filtered payment methods for the given site ID using a logging utility.
 * - Returns an object with the filtered payment methods or an empty array in case of an error.
 */
export const filterPaymentMethods = (
    response,
    countryCode,
    siteId,
    isExpress = false,
    basket,
    shipToCountry
) => {
    let paymentMethodsArray = []
    let isExpressShippingMethod = false
    let isOmnichannel = false

    try {
        isExpressShippingMethod = isExpressShipping(basket)
        isOmnichannel = isOmnichannelBasket(basket)
        const {paymentMethods} = response

        paymentMethods.forEach((method) => {
            let methodDetails = {type: method.type, name: method.name}

            if (
                ADYEN_BLOCKED_PAYMENT_METHODS.includes(method.type) ||
                method.type.indexOf('bankTransfer') > -1
            ) {
                return
            }

            switch (method.type) {
                case 'scheme':
                    // Filter for Amex, Visa, and Mastercard brands
                    // eslint-disable-next-line no-case-declarations
                    const validBrands = method.brands.filter((brand) =>
                        ['amex', 'visa', 'mc'].includes(brand)
                    )

                    if (validBrands.length > 0) {
                        methodDetails.brands = validBrands
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'paypal':
                    methodDetails.configuration = {
                        intent: 'authorize'
                    }

                    paymentMethodsArray.push(methodDetails)
                    break
                case 'bcmc':
                    if (countryCode === 'BE') {
                        if (method.brands) {
                            methodDetails.brands = method.brands
                        }
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'alipay':
                    if (shipToCountry === 'CN' && !isOmnichannel && !isExpressShippingMethod) {
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'wechatpayQR':
                    if (shipToCountry === 'CN') {
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'giropay':
                    if (countryCode === 'DE') {
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'klarna_account':
                    if (
                        ['US', 'GB', 'FR', 'IT', 'ES', 'AT', 'FI', 'DE', 'NO', 'SE', 'AU'].includes(
                            shipToCountry
                        )
                    ) {
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'applepay':
                    if (['US', 'FR'].includes(shipToCountry) && isExpress && !isOmnichannel) {
                        methodDetails.brands = method.brands
                        methodDetails.configuration = {
                            merchantId: method.configuration.merchantId,
                            merchantName: method.configuration.merchantName
                        }
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'eps':
                    if (countryCode === 'AT') {
                        if (method.issuers) {
                            methodDetails.issuers = []
                            methodDetails.issuers = methodDetails.issuers.concat(method.issuers)
                        }
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                case 'directEbanking':
                    if (['DE', 'AT', 'BE'].includes(countryCode) && !isExpressShippingMethod) {
                        paymentMethodsArray.push(methodDetails)
                    }
                    break
                default:
                    break
            }
        })

        return {paymentMethods: paymentMethodsArray}
    } catch (error) {
        return {paymentMethods: []}
    }
}
